<p-dialog
  [header]="'calendar.calendarsDialog.title' | transloco"
  [(visible)]="visible"
  [modal]="true"
  [dismissableMask]="true"
  [closable]="true"
  [draggable]="false"
  [resizable]="false"
  styleClass="w-full max-w-4xl"
  [style]="{ width: '90vw', height: '80vh' }"
>
  <div class="flex flex-col md:flex-row h-full overflow-hidden">
    <!-- Top/Left Sidebar - Menu -->
    <div
      class="w-full md:w-64 flex-shrink-0 md:border-e border-neutral-200 dark:border-neutral-600/30 mb-4 md:pe-4"
    >
      <p-menu [model]="menuItems" styleClass="w-full border-none">
        <ng-template pTemplate="item" let-item>
          <div
            class="menu-item-template flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 cursor-pointer"
            [ngClass]="{
              'selected bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-700':
                selectedCalendarType === item.data?.type,
              'hover:bg-neutral-100 dark:hover:bg-neutral-700/50 text-neutral-700 dark:text-neutral-300':
                selectedCalendarType !== item.data?.type,
            }"
            (click)="item.command && item.command()"
          >
            <i [class]="'menu-item-icon ' + item.icon + ' text-sm'"></i>
            <span class="text-sm font-medium">{{ item.label | transloco }}</span>
          </div>
        </ng-template>
      </p-menu>
    </div>

    <!-- Bottom/Right Content Area -->
    <div class="flex-1 md:ps-6 overflow-y-auto">
      <!-- Header with Search and Add Button -->
      <div class="mb-4 md:mb-6">
        <div class="flex items-center justify-between mb-4 hidden lg:block">
          <h3 class="text-base md:text-lg font-semibold text-neutral-900 dark:text-white">
            {{ selectedCalendarType.charAt(0).toUpperCase() + selectedCalendarType.slice(1) }}
            {{ 'calendar.calendarsDialog.calendars' | transloco }}
          </h3>
        </div>

        <!-- Search Box -->
        <div class="relative max-w-full">
          <div class="absolute inset-y-0 start-0 ps-3 flex items-center pointer-events-none">
            <i class="pi pi-search text-neutral-400 text-sm"></i>
          </div>
          <input
            type="text"
            [placeholder]="'calendar.calendarsDialog.searchCalendars' | transloco"
            [(ngModel)]="searchQuery"
            (input)="onSearchInput()"
            class="w-full ps-10 pe-10 py-2 bg-white dark:bg-neutral-700 border border-neutral-300 dark:border-neutral-600 rounded-lg text-sm text-neutral-800 dark:text-neutral-200 placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
          />
          @if (searchQuery) {
            <button
              (click)="clearSearch()"
              class="absolute inset-y-0 end-0 pe-3 flex items-center text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 cursor-pointer"
            >
              <i class="pi pi-times text-sm"></i>
            </button>
          }
        </div>
      </div>

      <!-- Calendar List -->
      <div class="space-y-2 md:space-y-3">
        @if (getFilteredCalendars().length > 0) {
          @for (calendar of getFilteredCalendars(); track calendar.id) {
            <div
              class="flex items-center justify-between p-3 md:p-4 border border-neutral-200 dark:border-neutral-600 rounded-lg hover:bg-neutral-200/80 dark:hover:bg-neutral-700/80 transition-colors cursor-pointer"
            >
              <div class="flex items-center gap-3">
                <div class="w-4 h-4 rounded" [style.background-color]="calendar.color"></div>
                <div>
                  <h4 class="font-medium text-neutral-900 dark:text-white">{{ calendar.name }}</h4>
                  <p class="text-sm text-neutral-500 dark:text-neutral-400">
                    {{ calendar.events.length }} {{ 'calendar.calendarsDialog.events' | transloco }}
                  </p>
                </div>
              </div>
              <div class="flex items-center gap-2">
                <p-button
                  icon="pi pi-pencil"
                  size="small"
                  [text]="true"
                  [rounded]="true"
                  severity="secondary"
                  [pTooltip]="'calendar.calendarsDialog.editCalendar' | transloco"
                  (click)="showEditCalendarForm(calendar)"
                >
                </p-button>
                <p-button
                  icon="pi pi-trash"
                  size="small"
                  [text]="true"
                  [rounded]="true"
                  severity="danger"
                  [pTooltip]="'calendar.calendarsDialog.deleteCalendar' | transloco"
                  (click)="deleteCalendar(calendar)"
                >
                </p-button>
              </div>
            </div>
          }
          <div
            class="flex items-center justify-between px-3 md:px-4 py-4 md:py-5 border border-dashed border-primary-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-200/80 dark:hover:bg-neutral-700/80 transition-colors cursor-pointer"
            (click)="showNewCalendarFormDialog()"
          >
            <div class="flex items-center justify-center text-center w-full text-primary-600">
              <i class="pi pi-plus text-sm me-2"></i>
              <span class="text-sm md:text-base">{{
                'calendar.calendarsDialog.addNewCalendar' | transloco
              }}</span>
            </div>
          </div>
        } @else {
          <div class="text-center py-12">
            <i class="pi pi-calendar text-4xl text-neutral-400 mb-4"></i>
            <h4 class="text-lg font-medium text-neutral-900 dark:text-white mb-2">
              @if (searchQuery) {
                {{ 'calendar.calendarsDialog.noCalendarsFound' | transloco }}
              } @else {
                {{
                  'calendar.calendarsDialog.noCalendarsOfType'
                    | transloco: { type: selectedCalendarType }
                }}
              }
            </h4>
            <p class="text-neutral-500 dark:text-neutral-400 mb-4">
              @if (searchQuery) {
                {{ 'calendar.calendarsDialog.tryAdjustingSearch' | transloco }}
              } @else {
                {{
                  'calendar.calendarsDialog.createFirstCalendar'
                    | transloco: { type: selectedCalendarType }
                }}
              }
            </p>
            @if (!searchQuery) {
              <p-button
                [label]="'calendar.calendarsDialog.addCalendar' | transloco"
                icon="pi pi-plus"
                size="small"
                (click)="showNewCalendarFormDialog()"
              >
              </p-button>
            }
          </div>
        }
      </div>
    </div>
  </div>

  <!-- New Calendar Form Dialog -->
  <p-dialog
    [header]="
      editingCalendar
        ? ('calendar.calendarsDialog.editCalendarTitle' | transloco)
        : ('calendar.calendarsDialog.addNewCalendarTitle' | transloco)
    "
    [(visible)]="showNewCalendarForm"
    [modal]="false"
    [closable]="true"
    [draggable]="false"
    [resizable]="false"
    styleClass="w-full max-w-md"
    [style]="{ width: '400px' }"
  >
    <div class="space-y-4">
      <!-- Calendar Name -->
      <div class="space-y-2">
        <label class="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
          {{ 'calendar.calendarsDialog.calendarName' | transloco }}
        </label>
        <input
          type="text"
          [(ngModel)]="newCalendar.name"
          [placeholder]="'calendar.calendarsDialog.enterCalendarName' | transloco"
          class="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-transparent transition"
        />
      </div>

      <!-- Calendar Color -->
      <div class="space-y-2">
        <label class="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
          {{ 'calendar.calendarsDialog.calendarColor' | transloco }}
        </label>
        <div class="flex flex-wrap gap-2">
          @for (color of colorOptions; track color.value) {
            <button
              type="button"
              (click)="newCalendar.color = color.value"
              [class]="
                'w-8 h-8 rounded-md border-2 transition-all hover:scale-110 ' +
                (newCalendar.color === color.value
                  ? 'border-neutral-800 dark:border-white'
                  : 'border-neutral-300 dark:border-neutral-600')
              "
              [style.background-color]="color.value"
              [title]="color.label"
            ></button>
          }
        </div>
      </div>
    </div>

    <ng-template pTemplate="footer">
      <div class="flex justify-end gap-2">
        <p-button
          [label]="'calendar.calendarsDialog.cancel' | transloco"
          severity="secondary"
          size="small"
          (click)="cancelNewCalendar()"
        >
        </p-button>
        <p-button
          [label]="
            editingCalendar
              ? ('calendar.calendarsDialog.update' | transloco)
              : ('calendar.calendarsDialog.create' | transloco)
          "
          size="small"
          (click)="saveCalendar()"
          [disabled]="!newCalendar.name.trim()"
        >
        </p-button>
      </div>
    </ng-template>
  </p-dialog>

  <!-- Confirm Dialog -->
  <p-confirmDialog></p-confirmDialog>
</p-dialog>
